import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Cpu, Eye, EyeOff, Loader2, CheckCircle2, XCircle, Trash2, Save, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

var eurFmt = function(v) {
  return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
};

function BudgetOverview({ data, onSaved }) {
  var [budget, setBudget] = useState(String(data.budget_eur || 50));
  var [saving, setSaving] = useState(false);

  function saveBudget() {
    setSaving(true);
    settingsAPI.updateAiBudget({ monthly_budget_eur: parseFloat(budget) || 0 })
      .then(function() { toast.success('Gesamt-Budget gespeichert'); if (onSaved) onSaved(); })
      .catch(function() { toast.error('Fehler beim Speichern'); })
      .finally(function() { setSaving(false); });
  }

  var used = data.cost_this_month_total_eur || 0;
  var limit = data.budget_eur || 0;
  var pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  var barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-blue-500';

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="ai-budget-overview">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-700">KI-Budget diesen Monat</p>
          <p className="text-xl font-bold text-slate-900">{eurFmt(used)} <span className="text-sm font-normal text-slate-500">/ {limit > 0 ? eurFmt(limit) : 'kein Limit'}</span></p>
        </div>
        {pct >= 90 && <AlertCircle className="h-5 w-5 text-red-500" />}
      </div>
      {limit > 0 && (
        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
          <div className={'h-2 rounded-full transition-all ' + barColor} style={{ width: pct + '%' }} />
        </div>
      )}
      <p className="text-xs text-slate-400">Bei Erreichen des Budgets werden KI-Funktionen automatisch pausiert, bis der neue Monat beginnt oder das Budget erhöht wird.</p>
      <div className="flex items-end gap-2">
        <div>
          <Label className="text-xs">Gesamt-Budget pro Monat (€)</Label>
          <Input type="number" step="0.01" min="0" value={budget} onChange={function(e) { setBudget(e.target.value); }} className="w-32 h-9" data-testid="ai-global-budget-input" />
        </div>
        <Button size="sm" onClick={saveBudget} disabled={saving} data-testid="save-ai-global-budget">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Speichern
        </Button>
      </div>
      <p className="text-xs text-slate-400">0 = kein Limit. Die Kosten basieren auf einer Preistabelle und sind Schätzwerte.</p>
    </div>
  );
}

function AIFunctionBlock({ fn, providers, onSaved }) {
  var [provider, setProvider] = useState(fn.provider || 'openai');
  var [model, setModel] = useState(fn.model || '');
  var [apiKey, setApiKey] = useState('');
  var [enabled, setEnabled] = useState(fn.enabled);
  var [showKey, setShowKey] = useState(false);
  var [saving, setSaving] = useState(false);
  var [testing, setTesting] = useState(false);
  var [testResult, setTestResult] = useState(null);
  var [funcBudget, setFuncBudget] = useState(String(fn.monthly_budget_eur || 0));

  function save() {
    setSaving(true);
    var body = { provider: provider, model: model, enabled: enabled, monthly_budget_eur: parseFloat(funcBudget) || 0 };
    if (apiKey.trim()) body.api_key = apiKey.trim();
    settingsAPI.updateAIConfig(fn.id, body).then(function() {
      toast.success('KI-Konfiguration gespeichert');
      setApiKey('');
      if (onSaved) onSaved();
    }).catch(function() { toast.error('Fehler beim Speichern'); }).finally(function() { setSaving(false); });
  }

  function removeKey() {
    setSaving(true);
    settingsAPI.updateAIConfig(fn.id, { provider: provider, model: model, enabled: false, remove_key: true }).then(function() {
      toast.success('Schlüssel entfernt');
      setEnabled(false);
      if (onSaved) onSaved();
    }).catch(function() { toast.error('Fehler'); }).finally(function() { setSaving(false); });
  }

  function testConnection() {
    var key = apiKey.trim() || fn.api_key;
    if (!key || !model) { toast.error('Modell und Schlüssel nötig'); return; }
    setTesting(true); setTestResult(null);
    settingsAPI.testAIConfig(fn.id, { provider: provider, model: model, api_key: key }).then(function(res) {
      setTestResult(res.data);
    }).catch(function() { setTestResult({ success: false, error: 'Netzwerkfehler' }); }).finally(function() { setTesting(false); });
  }

  var placeholders = { openai: 'gpt-4o-mini', anthropic: 'claude-3-5-haiku-latest', google: 'gemini-1.5-flash' };
  var lastUsed = fn.last_used_at ? new Date(fn.last_used_at).toLocaleString('de-DE') : 'noch nie';

  var providerItems = [];
  for (var i = 0; i < providers.length; i++) {
    providerItems.push(<SelectItem key={providers[i]} value={providers[i]}>{providers[i]}</SelectItem>);
  }

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-slate-900">{fn.label}</h4>
          <p className="text-xs text-slate-500">{fn.description}</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Anbieter</Label>
          <Select value={provider} onValueChange={function(v) { setProvider(v); setTestResult(null); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{providerItems}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Modell</Label>
          <Input value={model} onChange={function(e) { setModel(e.target.value); }} placeholder={placeholders[provider] || ''} />
        </div>
        <div>
          <Label className="text-xs">API-Schlüssel</Label>
          <div className="relative">
            <Input type={showKey ? 'text' : 'password'} value={apiKey}
              onChange={function(e) { setApiKey(e.target.value); }}
              placeholder={fn.configured ? '•••• gespeichert' : 'Schlüssel eingeben'}
              className="pr-10" />
            <button type="button" onClick={function() { setShowKey(!showKey); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="text-xs text-slate-400 space-x-4">
        <span>Hinterlegt: {fn.configured ? '\u2713' : '\u2717'}</span>
        <span>Zuletzt: {lastUsed}</span>
        <span>Tokens gesamt: {(fn.total_tokens || 0).toLocaleString('de-DE')}</span>
        <span>Diesen Monat: {(fn.tokens_this_month || 0).toLocaleString('de-DE')}</span>
        <span>Letzter Aufruf: {(fn.last_tokens || 0).toLocaleString('de-DE')}</span>
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span className="text-slate-600 font-medium" data-testid={'cost-this-month-' + fn.id}>Kosten diesen Monat: {eurFmt(fn.cost_this_month_eur)}</span>
        <div className="flex items-center gap-1">
          <Label className="text-xs text-slate-500">Budget (€):</Label>
          <Input type="number" step="0.01" min="0" value={funcBudget} onChange={function(e) { setFuncBudget(e.target.value); }} className="w-20 h-7 text-xs" data-testid={'func-budget-' + fn.id} />
          <span className="text-slate-400">(0 = kein Limit)</span>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={testConnection} disabled={testing} variant="outline">
          {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Testen
        </Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Speichern
        </Button>
        {fn.configured && (
          <button onClick={removeKey} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1">
            <Trash2 className="h-3 w-3" /> Schlüssel entfernen
          </button>
        )}
        {testResult && (
          <span className={'text-xs flex items-center gap-1 ' + (testResult.success ? 'text-green-600' : 'text-red-600')}>
            {testResult.success ? <><CheckCircle2 className="h-3 w-3" /> Verbindung OK ({testResult.tokens} Tokens)</> : <><XCircle className="h-3 w-3" /> {testResult.error}</>}
          </span>
        )}
      </div>
    </div>
  );
}

export function AISettings() {
  var [data, setData] = useState(null);

  function load() {
    settingsAPI.getAIConfigs().then(function(res) { setData(res.data); }).catch(function() {});
  }

  useEffect(load, []);

  if (!data) return null;

  var blocks = [];
  for (var i = 0; i < data.functions.length; i++) {
    blocks.push(<AIFunctionBlock key={data.functions[i].id} fn={data.functions[i]} providers={data.providers} onSaved={load} />);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Cpu className="h-5 w-5" /> KI-Funktionen</CardTitle>
        <CardDescription>API-Schlüssel und Modelle für KI-gestützte Funktionen verwalten</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <BudgetOverview data={data} onSaved={load} />
        {blocks}
      </CardContent>
    </Card>
  );
}
